/**
 * Calculate similarity between two strings.
 * Returns a value between 0 (no similarity) and 1 (identical).
 */
export const calculateStringSimilarity = (
  str1: string,
  str2: string
): number => {
  if (str1 === str2) return 1.0;
  if (str1.length === 0 || str2.length === 0) return 0.0;

  // Normalize strings: remove extra whitespace, convert to lowercase
  const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalized1 = normalize(str1);
  const normalized2 = normalize(str2);

  if (normalized1 === normalized2) return 1.0;

  // Check if one contains the other (substring match)
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    const shorter = Math.min(normalized1.length, normalized2.length);
    const longer = Math.max(normalized1.length, normalized2.length);
    return shorter / longer; // Ratio of shorter to longer
  }

  // Calculate similarity based on common characters (bigram approach)
  const getBigrams = (s: string): Set<string> => {
    const bigrams = new Set<string>();
    for (let i = 0; i < s.length - 1; i++) {
      bigrams.add(s.substring(i, i + 2));
    }
    return bigrams;
  };

  const bigrams1 = getBigrams(normalized1);
  const bigrams2 = getBigrams(normalized2);

  if (bigrams1.size === 0 && bigrams2.size === 0) {
    // Both strings are too short for bigrams, use character-level similarity
    const chars1 = new Set(normalized1.split(''));
    const chars2 = new Set(normalized2.split(''));
    const intersection = new Set([...chars1].filter((c) => chars2.has(c)));
    const union = new Set([...chars1, ...chars2]);
    return union.size > 0 ? intersection.size / union.size : 0;
  }

  const intersection = new Set([...bigrams1].filter((b) => bigrams2.has(b)));
  const union = new Set([...bigrams1, ...bigrams2]);

  return union.size > 0 ? intersection.size / union.size : 0;
};
